import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useAppStore, type ApiKeys } from "../store/useAppStore";
import { useLocation, useNavigate } from "react-router-dom";
import { DocumentManager } from "../components/DocumentManager";
import { ReportProblemButton } from "../components/ReportProblemButton";
import { useEffect, useState } from "react";
import { auth } from "../lib/firebase";
import { apiJson } from "../lib/api";

export default function Settings() {
  const { businessName, followUpStyle, cadence, setOnboardingData, focus, setAuth, isPro, planId, apiKeys, setApiKeys, activeAiProvider, selectedModels, setSelectedModel, setPlanId } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [localKeys, setLocalKeys] = useState<ApiKeys>(apiKeys || {});
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isSavingKeys, setIsSavingKeys] = useState(false);
  const [isBillingPending, setIsBillingPending] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<"foundation" | "operator" | "atlas" | null>(null);

  const PLAN_LABELS: Record<string, string> = {
    free: "Free",
    foundation: "Foundation",
    operator: "Operator",
    atlas: "Atlas",
  };

  const BILLING_PLANS = [
    {
      id: "foundation" as const,
      label: "Foundation",
      price: "$29.99/mo",
      helper: "Perfect for testing the paid workflow and basic automation.",
    },
    {
      id: "operator" as const,
      label: "Operator",
      price: "$59.99/mo",
      helper: "For active users who want more throughput and automation.",
    },
    {
      id: "atlas" as const,
      label: "Atlas",
      price: "$119.99/mo",
      helper: "For power users who need the fullest workflow.",
    },
  ];

  const handleUpdateKey = (provider: keyof ApiKeys, value: string) => {
    setLocalKeys(prev => ({ ...prev, [provider]: value }));
  };

  const handleSaveKeys = async () => {
    try {
      setIsSavingKeys(true);
      await setApiKeys(localKeys);
      toast.success("API keys saved");
    } catch (error) {
      toast.error("Could not save API keys");
    } finally {
      setIsSavingKeys(false);
    }
  };

  const modelOptions: Record<string, { label: string, value: string }[]> = {
    gemini: [
      { label: "Gemini 1.5 Flash", value: "gemini-1.5-flash" },
      { label: "Gemini 1.5 Pro", value: "gemini-1.5-pro" },
    ],
    openai: [
      { label: "GPT-4o Mini", value: "gpt-4o-mini" },
      { label: "GPT-4o", value: "gpt-4o" },
    ],
    claude: [
      { label: "Claude 3.5 Sonnet", value: "claude-3-5-sonnet-20240620" },
      { label: "Claude 3 Haiku", value: "claude-3-haiku-20240307" },
    ],
    ollama: [
      { label: "Llama 3 (8B)", value: "llama3" },
      { label: "Mistral", value: "mistral" },
    ],
    qwen: [
      { label: "Qwen Plus", value: "qwen-plus" },
      { label: "Qwen Max", value: "qwen-max" },
    ],
    opencode: [
      { label: "Meta Llama 3 8B Instruct", value: "meta-llama/Meta-Llama-3-8B-Instruct" },
      { label: "Mistral 7B Instruct v0.3", value: "mistralai/Mistral-7B-Instruct-v0.3" },
    ],
    openrouter: [
      { label: "OpenRouter Free Auto", value: "openrouter/free" },
      { label: "Qwen3 Coder Free", value: "qwen/qwen3-coder:free" },
      { label: "MiniMax M2.5 Free", value: "minimax/minimax-m2.5:free" },
      { label: "Elephant Alpha", value: "openrouter/elephant-alpha" },
      { label: "GPT OSS 120B Free", value: "openai/gpt-oss-120b:free" },
      { label: "Llama 3.3 70B Instruct", value: "meta-llama/llama-3.3-70b-instruct:free" },
    ],
  };

  const apiKeyFields: Array<{
    id: keyof ApiKeys;
    label: string;
    placeholder: string;
    helper: string;
    type?: string;
  }> = [
    { id: "gemini", label: "Google Gemini API Key", placeholder: "AIzaSy...", helper: "Used for Gemini models.", type: "password" },
    { id: "openai", label: "OpenAI API Key", placeholder: "sk-...", helper: "Used for GPT-4o and GPT-4o Mini.", type: "password" },
    { id: "claude", label: "Anthropic Claude API Key", placeholder: "sk-ant-...", helper: "Used for Claude models.", type: "password" },
    { id: "ollama", label: "Ollama URL (Local / Hosted)", placeholder: "http://localhost:11434", helper: "Point this at your Ollama server.", type: "url" },
    { id: "qwen", label: "Alibaba Qwen API Key", placeholder: "sk-...", helper: "Used for Qwen models.", type: "password" },
    { id: "opencode", label: "OpenCode API Key", placeholder: "sk-...", helper: "Used for OpenCode provider requests.", type: "password" },
    { id: "openrouter", label: "OpenRouter API Key", placeholder: "sk-or-v1-...", helper: "Used for OpenRouter free and paid models.", type: "password" },
  ];

  const activeModelOptions = modelOptions[activeAiProvider] || [];
  const currentModel = selectedModels[activeAiProvider] || (activeModelOptions[0]?.value || "");
  const planBadgeLabel = isBillingPending ? `Verification pending${pendingPlan ? `: ${PLAN_LABELS[pendingPlan]}` : ""}` : `${PLAN_LABELS[planId] || "Free"} plan active`;
  const planBadgeVariant = isBillingPending ? "secondary" : isPro ? "default" : "outline";

  useEffect(() => {
    setLocalKeys(apiKeys || {});
  }, [apiKeys]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get("checkout");
    const plan = params.get("plan") as "foundation" | "operator" | "atlas" | null;
    if (checkoutStatus === "success") {
      setPendingPlan(plan);
      setIsBillingPending(true);
      toast.success(`Stripe checkout completed for ${plan ? PLAN_LABELS[plan] : "your plan"}. Activating now...`);
      navigate("/settings", { replace: true });
    }
    if (checkoutStatus === "cancelled") {
      setPendingPlan(null);
      toast.message("Checkout cancelled");
      navigate("/settings", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (isPro) {
      setIsBillingPending(false);
      setPendingPlan(null);
    }
  }, [isPro]);

  const handleUpgrade = (targetPlan: "foundation" | "operator" | "atlas") => {
    const checkoutRoute = `/checkout/${targetPlan}?returnTo=/settings`;
    const currentUser = auth.currentUser;
    if (!currentUser) {
      toast.error("You need to be signed in before upgrading.");
      navigate(`/login?redirect=${encodeURIComponent(checkoutRoute)}`);
      return;
    }

    setIsCheckoutLoading(true);
    navigate(checkoutRoute, { state: { source: "settings" } });
  };

  const handleManageBilling = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      toast.error("You need to be signed in to manage billing.");
      navigate("/login?redirect=/settings");
      return;
    }

    if (planId === "free") {
      toast.message("You don’t have an active subscription to cancel.");
      return;
    }

    try {
      setIsPortalLoading(true);
      const response = await apiJson<{ url?: string; error?: string }>("/api/stripe/create-billing-portal-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: currentUser.uid }),
      });

      if (!response.url) {
        throw new Error(response.error || "Billing portal did not return a URL");
      }

      window.location.assign(response.url);
    } catch (error: any) {
      toast.error(error?.message || "Could not open billing portal");
    } finally {
      setIsPortalLoading(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-5 px-5 py-6 sm:px-8 lg:grid-cols-[1.1fr_.9fr] lg:items-end lg:px-10 lg:py-8">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <span>Workspace</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>Settings</span>
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Settings</h1>
            <p className="max-w-2xl text-sm leading-6 text-slate-500">Manage your CRM preferences, billing, and integrations from one structured control center.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Plan</p>
              <p className="mt-1 text-2xl font-semibold text-slate-950">{PLAN_LABELS[planId] || "Free"}</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-500">Status</p>
              <p className="mt-1 text-sm font-medium text-emerald-700">{planBadgeLabel}</p>
            </div>
          </div>
        </div>
      </div>

      <Card className="shrink-0 overflow-hidden rounded-3xl border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle>Business Profile</CardTitle>
              <CardDescription>Used to tailor outreach, reminders, and AI-generated messaging.</CardDescription>
            </div>
            <Badge variant="outline" className="w-fit text-slate-600">Saved as you edit</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="bname">Business Name</Label>
              <Input
                id="bname"
                value={businessName}
                autoComplete="organization"
                onChange={(e) => setOnboardingData({ businessName: e.target.value, followUpStyle, cadence, focus, activeAiProvider }, true)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="focus">Wholesaling Focus</Label>
              <Select value={focus} onValueChange={(val) => setOnboardingData({ businessName, followUpStyle, cadence, focus: val, activeAiProvider }, true)}>
                <SelectTrigger id="focus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vacant houses">Vacant houses</SelectItem>
                  <SelectItem value="Tax delinquent">Tax delinquent</SelectItem>
                  <SelectItem value="Probate">Probate</SelectItem>
                  <SelectItem value="Tired landlords">Tired landlords</SelectItem>
                  <SelectItem value="Driving for dollars">Driving for dollars</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-lg border bg-slate-50/80 px-3 py-2 text-xs leading-5 text-slate-600">
            These settings help keep tone, targeting, and follow-up language consistent across the app.
          </div>
        </CardContent>
        <CardFooter>
          <p className="text-sm text-slate-500">Profile changes save as you edit.</p>
        </CardFooter>
      </Card>

      <Card className="shrink-0">
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle>Follow-Up Rules</CardTitle>
              <CardDescription>Choose the tone, cadence, and AI provider used for outreach.</CardDescription>
            </div>
            <Badge variant="outline" className="w-fit text-slate-600">Applied to new work</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="tone">AI Tone of Voice</Label>
              <Select value={followUpStyle} onValueChange={(val) => setOnboardingData({ businessName, followUpStyle: val, cadence, focus, activeAiProvider }, true)}>
                <SelectTrigger id="tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Friendly">Friendly</SelectItem>
                  <SelectItem value="Professional">Professional</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                  <SelectItem value="Investor-style">Investor-style</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cadence">Default Cadence</Label>
              <Select value={cadence} onValueChange={(val) => setOnboardingData({ businessName, followUpStyle, cadence: val, focus, activeAiProvider }, true)}>
                <SelectTrigger id="cadence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="provider">Active AI Provider</Label>
            <div className="flex flex-col gap-2 lg:flex-row">
              <Select value={activeAiProvider} onValueChange={(val) => setOnboardingData({ businessName, followUpStyle, cadence, focus, activeAiProvider: val }, true)}>
                <SelectTrigger id="provider" className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gemini">Google Gemini</SelectItem>
                  <SelectItem value="openai">OpenAI (ChatGPT)</SelectItem>
                  <SelectItem value="claude">Anthropic Claude</SelectItem>
                  <SelectItem value="ollama">Ollama (Local / Custom)</SelectItem>
                  <SelectItem value="qwen">Alibaba Qwen</SelectItem>
                  <SelectItem value="opencode">OpenCode</SelectItem>
                  <SelectItem value="openrouter">OpenRouter (Free Models)</SelectItem>
                </SelectContent>
              </Select>
              {activeModelOptions.length > 0 && (
                <Select value={currentModel} onValueChange={(val) => setSelectedModel(activeAiProvider, val)}>
                  <SelectTrigger className="flex-1 border-blue-200 bg-blue-50">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeModelOptions.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="rounded-lg border bg-slate-50/80 px-3 py-2 text-xs leading-5 text-slate-600">
            The provider and model below determine the tone and quality of generated follow-ups.
          </div>
        </CardContent>
        <CardFooter>
          <p className="text-sm text-slate-500">Rules save as you edit.</p>
        </CardFooter>
      </Card>

      <Card className="shrink-0">
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle>AI Provider API Keys</CardTitle>
              <CardDescription>Bring your own keys for AI providers. Values stay hidden after you save them.</CardDescription>
            </div>
            <Badge variant="outline" className="w-fit text-slate-600">{isSavingKeys ? "Saving…" : "Stored privately"}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-slate-50/80 px-3 py-2 text-xs leading-5 text-slate-600">
            Leave a key as-is if you don’t want to replace it. Blank fields keep the current saved secret, and browser autofill is disabled here to avoid accidental changes.
          </div>
          <div className="grid gap-4">
            {apiKeyFields.map((field) => (
              <div className="grid gap-2" key={field.id}>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor={`key-${field.id}`}>{field.label}</Label>
                  {localKeys?.[field.id] ? (
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-slate-500">Saved</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">Not set</Badge>
                  )}
                </div>
                <Input
                  id={`key-${field.id}`}
                  type={field.type || "password"}
                  value={localKeys?.[field.id] || ""}
                  onChange={(e) => handleUpdateKey(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  autoComplete="new-password"
                  spellCheck={false}
                />
                <p className="text-xs text-slate-500">{field.helper}</p>
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={handleSaveKeys} disabled={isSavingKeys}>
            {isSavingKeys ? "Saving keys..." : "Save API keys"}
          </Button>
        </CardFooter>
      </Card>

      <Card className="shrink-0 relative overflow-hidden border-blue-100">
        <div className="absolute top-0 right-0 p-6 pointer-events-none opacity-10">
           <svg className="w-24 h-24 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        </div>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle>Subscription</CardTitle>
              <CardDescription>
                {isBillingPending
                  ? `Payment received for ${pendingPlan ? PLAN_LABELS[pendingPlan] : "your chosen plan"}. Waiting for Stripe webhook confirmation.`
                  : planId === "free"
                    ? "Free tier — upgrade anytime with Stripe Checkout."
                    : `${PLAN_LABELS[planId] || "Your"} plan is active and billing is in good standing.`}
              </CardDescription>
            </div>
            <Badge variant={planBadgeVariant as "default" | "outline"} className="w-fit">
              {planBadgeLabel}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-slate-50/80 px-3 py-3 text-sm text-slate-700">
            {isBillingPending ? (
              <div className="flex items-start gap-3">
                <svg className="mt-0.5 h-4 w-4 animate-spin text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8 8 0 104.582 9m15.356 2H15" /></svg>
                <p>Stripe checkout completed. We’re waiting for the webhook to confirm the plan on this account.</p>
              </div>
            ) : isPro ? (
              <div className="flex items-start gap-3">
                <svg className="mt-0.5 h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                <p>Stripe billing has confirmed your access. Your account keeps paid features enabled automatically.</p>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <svg className="mt-0.5 h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 21a9 9 0 100-18 9 9 0 000 18z" /></svg>
                <p>Upgrade with Stripe Checkout. When payment completes, the webhook updates this account automatically.</p>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-800">Plan Features:</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {planId === "free" ? "Free tier includes basic lead management." : "Unlimited active leads"}
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-600">
                {isPro ? (
                  <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                )}
                AI Follow-Up Text Generation
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {planId === "free" ? "Pipeline preview only" : "Follow-up reminders & Pipeline view"}
              </li>
            </ul>
          </div>
        </CardContent>
        <CardFooter className="bg-slate-50 border-t pt-4">
          <div className="flex w-full flex-col gap-3 text-sm">
            <div className="grid gap-2 sm:grid-cols-3">
              {BILLING_PLANS.map((tier) => (
                <Button
                  key={tier.id}
                  type="button"
                  variant={planId === tier.id ? "secondary" : "default"}
                  className="h-auto flex-col items-start gap-1 px-3 py-3 text-left"
                  onClick={() => handleUpgrade(tier.id)}
                  disabled={isCheckoutLoading || isPortalLoading}
                >
                  <span className="text-sm font-semibold">{tier.label}</span>
                  <span className="text-xs opacity-80">{tier.price}</span>
                </Button>
              ))}
            </div>
            <div className="rounded-lg border bg-white px-3 py-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-800">Need to cancel or update billing?</p>
                  <p className="text-xs leading-5 text-slate-500">Open Stripe’s billing portal to cancel, switch plans, or update payment details.</p>
                </div>
                {planId === "free" ? (
                  <Badge variant="outline" className="w-fit text-slate-500">No active subscription</Badge>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleManageBilling}
                    disabled={isPortalLoading}
                    className="whitespace-nowrap"
                  >
                    {isPortalLoading ? "Opening billing portal…" : "Manage / cancel subscription"}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
              <p>{planId === "free" ? "Choose a paid tier to upgrade." : `Current plan: ${PLAN_LABELS[planId] || "Free"}`}</p>
              <p>{isCheckoutLoading ? "Redirecting to Stripe…" : "Webhook activation happens automatically after payment."}</p>
            </div>
          </div>
        </CardFooter>
      </Card>

      <DocumentManager 
        entityId="account" 
        entityType="account" 
        title="Business Documents" 
        description="Upload standardized forms, assignment contracts, or proof of funds." 
      />

      <Card className="shrink-0 border-slate-200">
        <CardHeader>
          <CardTitle>Need help?</CardTitle>
          <CardDescription>Send a problem report from this account so we can trace the issue faster in Sentry and your workspace history.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-600">Use this when something breaks, looks wrong, or feels confusing during your workflow.</p>
          <ReportProblemButton buttonLabel="Report a problem" triggerVariant="outline" />
        </CardContent>
      </Card>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button variant="destructive" onClick={() => {
            setAuth(false, null);
            navigate('/login');
        }}>Log Out</Button>
        <Button variant="outline" onClick={() => navigate('/unsubscribe')}>
          Unsubscribe / manage billing
        </Button>
      </div>
    </div>
  );
}
